import { useMemo, useState } from 'react';
import { useStore } from '../state/store';
import { TopBar } from '../components/TopBar';
import {
  Banner,
  Button,
  Card,
  Chip,
  EmptyState,
  Icon,
  Label,
  Money,
  SectionHeader,
  Toggle,
} from '../ui';
import type { EventItem, Scenario } from '../types';
import { EVENT_META, EVENT_KINDS_BY_GROUP, blankEvent, eventHeadline } from '../events/meta';
import { TEMPLATES, duplicateScenario, scenarioFromTemplate } from '../events/templates';
import { EventEditor } from '../components/events/EventEditor';
import { NetWorthChart, type ChartEvent } from '../components/NetWorthChart';
import { LoansStrip } from '../components/LoansStrip';
import { project } from '../engine/projection';
import { fmtCompact } from '../utils/format';

interface Props {
  onGoTo: (tab: string) => void;
}

export function ScenariosPage({ onGoTo }: Props) {
  const {
    scenarios,
    activeScenario,
    activeScenarioId,
    compareScenarioIds,
    assumptions,
    accounts,
    currentBalances,
    setActiveScenario,
    toggleCompareScenario,
    saveScenario,
    deleteScenario,
  } = useStore();

  const [selectedId, setSelectedId] = useState<string>(activeScenarioId);
  const [showTemplates, setShowTemplates] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [showAddEvent, setShowAddEvent] = useState(false);

  // Projection for every visible scenario, once — used for impact cards.
  const projections = useMemo(() => {
    const out: Record<string, ReturnType<typeof project>> = {};
    for (const s of scenarios) {
      if (s.archived) continue;
      out[s.id] = project({
        assumptions,
        accounts,
        currentBalances,
        scenario: s,
      });
    }
    return out;
  }, [scenarios, assumptions, accounts, currentBalances]);

  const activeProj = projections[activeScenarioId];
  const retireIdx = Math.max(
    0,
    Math.min(
      assumptions.targetRetirementAge - assumptions.currentAge,
      (activeProj?.years.length ?? 1) - 1,
    ),
  );
  const activeNestEgg = activeProj?.years[retireIdx]?.netWorth ?? 0;

  const selected = scenarios.find(s => s.id === selectedId) ?? activeScenario;
  const selectedProj = projections[selected.id];
  const visibleScenarios = scenarios.filter(s => !s.archived).sort((a, b) => a.order - b.order);

  async function addTemplate(templateId: string) {
    const tpl = TEMPLATES.find(t => t.id === templateId);
    if (!tpl) return;
    const s = scenarioFromTemplate(tpl, assumptions, visibleScenarios.length);
    await saveScenario(s);
    setSelectedId(s.id);
    setShowTemplates(false);
  }

  async function duplicate(id: string) {
    const src = scenarios.find(s => s.id === id);
    if (!src) return;
    const copy = duplicateScenario(src, visibleScenarios.length);
    await saveScenario(copy);
    setSelectedId(copy.id);
  }

  async function remove(id: string) {
    if (id === 'baseline') {
      // Don't delete the anchor scenario — rename/edit instead.
      return;
    }
    if (!confirm('Delete this scenario?')) return;
    await deleteScenario(id);
    if (selectedId === id) setSelectedId(activeScenarioId);
  }

  async function addEvent(kind: EventItem['kind']) {
    const e = blankEvent(kind, 1);
    const scenario = selected;
    const next: Scenario = { ...scenario, events: [...scenario.events, e] };
    await saveScenario(next);
    setEditingEventId(e.id);
    setShowAddEvent(false);
  }

  async function patchEvent(next: EventItem) {
    const scenario = selected;
    const nextEvents = scenario.events.map(e => (e.id === next.id ? next : e));
    await saveScenario({ ...scenario, events: nextEvents });
  }

  async function removeEvent(id: string) {
    const scenario = selected;
    await saveScenario({ ...scenario, events: scenario.events.filter(e => e.id !== id) });
    if (editingEventId === id) setEditingEventId(null);
  }

  return (
    <>
      <TopBar
        title="Scenarios"
        subtitle={`${visibleScenarios.length} plans · ${compareScenarioIds.length} comparing`}
        onManageScenarios={() => onGoTo('scenarios')}
        right={
          <Button
            variant="primary"
            size="sm"
            leading={<Icon name="plus" size={14} />}
            onClick={() => setShowTemplates(v => !v)}
          >
            New
          </Button>
        }
      />
      <main className="mx-auto max-w-7xl space-y-6 px-4 py-4 pb-nav lg:px-6">
        {/* Live chart of the SELECTED scenario — stays visible while the
            user edits its events below. Net-worth only; the full multi-tab
            chart lives on the Plan page. */}
        {selectedProj && (
          <>
            <section className="card card-dark p-3">
              <div className="mb-1 flex items-center justify-between px-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
                  Projection — {selected.name}
                </span>
                <span className="text-[11px] text-ink-500 dark:text-ink-300">
                  {selected.id !== activeScenarioId ? 'Preview (not active)' : 'Active scenario'}
                </span>
              </div>
              <NetWorthChart
                years={selectedProj.years}
                targetRetirementAge={assumptions.targetRetirementAge}
                requiredNestEgg={selectedProj.requiredNestEgg}
                startingCalendarYear={assumptions.startingCalendarYear}
                currentAge={assumptions.currentAge}
                chartEvents={selected.events
                  .filter(e => e.enabled !== false)
                  .map<ChartEvent>(e => {
                    const meta = EVENT_META[e.kind];
                    return {
                      yearOffset: e.yearOffset,
                      calendarYear: assumptions.startingCalendarYear + e.yearOffset,
                      name: ('name' in e && e.name) || meta.label,
                      kind: e.kind,
                      tone: meta.tone,
                    };
                  })}
              />
            </section>
            <LoansStrip
              years={selectedProj.years}
              mortgage={selectedProj.mortgage}
              startingCalendarYear={assumptions.startingCalendarYear}
              totalYears={selectedProj.years.length}
              onOpenEvent={id => {
                // Expand the event in the currently selected scenario.
                // (Loans in the strip correspond to events in this scenario.)
                setEditingEventId(id);
              }}
            />
          </>
        )}

      <div className="gap-6 lg:grid lg:grid-cols-[360px_minmax(0,1fr)]">
        {/* ── Scenario list column ────────────────────────────────────── */}
        <section className="space-y-3">
          {showTemplates && (
            <TemplatePicker
              onPick={addTemplate}
              onClose={() => setShowTemplates(false)}
            />
          )}
          <ul className="space-y-2">
            {visibleScenarios.map(s => {
              const isActive = s.id === activeScenarioId;
              const isSelected = s.id === selectedId;
              const inCompare = compareScenarioIds.includes(s.id);
              const proj = projections[s.id];
              const nestEgg = proj?.years[retireIdx]?.netWorth ?? 0;
              const delta = nestEgg - activeNestEgg;
              return (
                <li key={s.id}>
                  <ScenarioCard
                    scenario={s}
                    isActive={isActive}
                    isSelected={isSelected}
                    inCompare={inCompare}
                    nestEgg={nestEgg}
                    deltaVsActive={isActive ? null : delta}
                    onSelect={() => setSelectedId(s.id)}
                    onActivate={() => setActiveScenario(s.id)}
                    onToggleCompare={() => toggleCompareScenario(s.id)}
                    onDuplicate={() => duplicate(s.id)}
                    onDelete={() => remove(s.id)}
                  />
                </li>
              );
            })}
          </ul>
        </section>

        {/* ── Scenario detail column ─────────────────────────────────── */}
        <section className="mt-4 space-y-4 lg:mt-0">
          <ScenarioHeader
            scenario={selected}
            onRename={name => saveScenario({ ...selected, name })}
            onDescribe={d => saveScenario({ ...selected, description: d || undefined })}
            onColor={c => saveScenario({ ...selected, color: c })}
            onIcon={ic => saveScenario({ ...selected, icon: ic })}
          />

          {/* Events list */}
          <Card flush>
            <SectionHeader
              title={`Timeline · ${selected.events.length} event${selected.events.length === 1 ? '' : 's'}`}
              subtitle={selectedProj ? `Projects ${selectedProj.years.length} years forward` : undefined}
              bordered
              muted
              right={
                <Button
                  size="sm"
                  variant="outline"
                  leading={<Icon name="plus" size={12} />}
                  onClick={() => setShowAddEvent(true)}
                >
                  Add event
                </Button>
              }
            />
            {showAddEvent && (
              <div className="border-b border-ink-100 p-3 dark:border-ink-700">
                <EventKindPicker
                  onPick={addEvent}
                  onClose={() => setShowAddEvent(false)}
                />
              </div>
            )}
            {selected.events.length === 0 && !showAddEvent ? (
              <div className="p-4">
                <EmptyState
                  icon="🗻"
                  title="No events yet"
                  description="Add a purchase, income change, or any life event to see its effect on the projection."
                  action={
                    <Button
                      size="sm"
                      leading={<Icon name="plus" size={12} />}
                      onClick={() => setShowAddEvent(true)}
                    >
                      Add first event
                    </Button>
                  }
                />
              </div>
            ) : (
              <EventsTimeline
                events={selected.events}
                editingId={editingEventId}
                onEdit={id => setEditingEventId(editingEventId === id ? null : id)}
                onChange={patchEvent}
                onToggleEnabled={async e =>
                  patchEvent({ ...e, enabled: !e.enabled } as EventItem)
                }
                onDelete={removeEvent}
                onCloseEditor={() => setEditingEventId(null)}
                calendarYearOf={y => assumptions.startingCalendarYear + y}
                ageOf={y => assumptions.currentAge + y}
              />
            )}
          </Card>

          {selected.id !== activeScenarioId && (
            <Banner tone="info" icon="★" title="Not the active scenario" actions={
              <Button size="sm" onClick={() => setActiveScenario(selected.id)}>
                Make active
              </Button>
            }>
              Changes here only affect the projection once you make this scenario active.
            </Banner>
          )}
        </section>
      </div>
      </main>
    </>
  );
}

// ── Scenario card ───────────────────────────────────────────────────────────

function ScenarioCard({
  scenario,
  isActive,
  isSelected,
  inCompare,
  nestEgg,
  deltaVsActive,
  onSelect,
  onActivate,
  onToggleCompare,
  onDuplicate,
  onDelete,
}: {
  scenario: Scenario;
  isActive: boolean;
  isSelected: boolean;
  inCompare: boolean;
  nestEgg: number;
  deltaVsActive: number | null;
  onSelect: () => void;
  onActivate: () => void;
  onToggleCompare: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className={`rounded-2xl border transition ${
        isSelected
          ? 'border-accent-500 shadow-card dark:border-accent-500'
          : 'border-ink-100 hover:border-ink-200 dark:border-ink-700 dark:hover:border-ink-600'
      } bg-white dark:bg-ink-800`}
    >
      <button
        type="button"
        onClick={onSelect}
        className="flex w-full items-center gap-3 p-3 text-left"
      >
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg"
          style={{ backgroundColor: `${scenario.color}22`, color: scenario.color }}
          aria-hidden
        >
          {scenario.icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-sm font-semibold">{scenario.name}</span>
            {isActive && <Chip tone="accent" size="sm">Active</Chip>}
            {inCompare && !isActive && <Chip tone="neutral" size="sm">Compare</Chip>}
          </div>
          {scenario.description && (
            <div className="truncate text-[11px] text-ink-500 dark:text-ink-300">
              {scenario.description}
            </div>
          )}
          <div className="mt-1 flex items-center gap-3 text-[11px] text-ink-500 dark:text-ink-300">
            <span>{scenario.events.length} event{scenario.events.length === 1 ? '' : 's'}</span>
            {nestEgg > 0 && (
              <span>
                NW @ retire <Money value={nestEgg} compact tone="default" size="xs" />
              </span>
            )}
          </div>
        </div>
        {deltaVsActive != null && Math.abs(deltaVsActive) > 1_000 && (
          <div className="shrink-0 text-right">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
              Δ vs active
            </div>
            <div
              className={`font-mono text-sm font-bold tabular-nums ${
                deltaVsActive >= 0
                  ? 'text-pos-700 dark:text-pos-500'
                  : 'text-neg-700 dark:text-neg-500'
              }`}
            >
              {deltaVsActive >= 0 ? '+' : '−'}${fmtCompact(Math.abs(deltaVsActive))}
            </div>
          </div>
        )}
      </button>
      <div className="flex items-center gap-1 border-t border-ink-100 bg-ink-50/40 px-2 py-1.5 text-xs dark:border-ink-700 dark:bg-ink-800/50">
        {!isActive && (
          <button
            type="button"
            onClick={onActivate}
            className="rounded-md px-2 py-1 font-semibold text-accent-600 hover:bg-accent-50 dark:text-accent-500 dark:hover:bg-ink-700"
          >
            Make active
          </button>
        )}
        {!isActive && (
          <button
            type="button"
            onClick={onToggleCompare}
            className={`rounded-md px-2 py-1 font-semibold transition ${
              inCompare
                ? 'bg-accent-600 text-white hover:bg-accent-700'
                : 'text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-700'
            }`}
          >
            {inCompare ? '✓ Comparing' : 'Compare'}
          </button>
        )}
        <div className="flex-1" />
        <button
          type="button"
          onClick={onDuplicate}
          className="rounded-md px-2 py-1 font-semibold text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-700"
          title="Duplicate"
        >
          <Icon name="copy" size={14} />
        </button>
        {scenario.id !== 'baseline' && (
          <button
            type="button"
            onClick={onDelete}
            className="rounded-md px-2 py-1 font-semibold text-ink-600 hover:bg-neg-50 hover:text-neg-600 dark:text-ink-300 dark:hover:bg-neg-700/20"
            title="Delete"
          >
            <Icon name="trash" size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Scenario header (name / description / icon / color) ────────────────────

const COLOR_SWATCHES = [
  '#3b54e8', '#059669', '#f59e0b', '#dc2626', '#8b5cf6',
  '#0891b2', '#14b8a6', '#db2777', '#64748b',
];
const ICON_CHOICES = ['🗻', '🏡', '🛟', '🌴', '🏖️', '☕', '🍃', '🏠', '🛥️', '💰', '⏳', '🎯', '🔥', '🧭'];

function ScenarioHeader({
  scenario,
  onRename,
  onDescribe,
  onColor,
  onIcon,
}: {
  scenario: Scenario;
  onRename: (s: string) => void;
  onDescribe: (s: string) => void;
  onColor: (c: string) => void;
  onIcon: (i: string) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState<'icon' | 'color' | null>(null);
  return (
    <Card>
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => setPickerOpen(p => (p === 'icon' ? null : 'icon'))}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-xl transition hover:scale-105"
          style={{ backgroundColor: `${scenario.color}22`, color: scenario.color }}
          title="Change icon"
          aria-label="Change icon"
        >
          {scenario.icon}
        </button>
        <div className="min-w-0 flex-1 space-y-1">
          <input
            value={scenario.name}
            onChange={e => onRename(e.target.value)}
            placeholder="Scenario name"
            className="w-full rounded-md bg-transparent px-1 -mx-1 text-lg font-bold text-ink-900 outline-none hover:bg-ink-50 focus:bg-ink-50 dark:text-ink-50 dark:hover:bg-ink-700 dark:focus:bg-ink-700"
            aria-label="Scenario name"
          />
          <input
            value={scenario.description ?? ''}
            onChange={e => onDescribe(e.target.value)}
            placeholder="Short description (optional)"
            className="w-full rounded-md bg-transparent px-1 -mx-1 text-xs text-ink-500 outline-none hover:bg-ink-50 focus:bg-ink-50 dark:text-ink-300 dark:hover:bg-ink-700 dark:focus:bg-ink-700"
            aria-label="Scenario description"
          />
        </div>
        <button
          type="button"
          onClick={() => setPickerOpen(p => (p === 'color' ? null : 'color'))}
          className="h-8 w-8 shrink-0 rounded-full border-2 border-white shadow-sm ring-1 ring-ink-200 dark:border-ink-800 dark:ring-ink-700"
          style={{ backgroundColor: scenario.color }}
          title="Change color"
          aria-label="Change color"
        />
      </div>

      {pickerOpen === 'icon' && (
        <div className="mt-3 grid grid-cols-7 gap-1.5">
          {ICON_CHOICES.map(ic => (
            <button
              key={ic}
              type="button"
              onClick={() => {
                onIcon(ic);
                setPickerOpen(null);
              }}
              className={`flex h-9 items-center justify-center rounded-lg text-lg transition ${
                ic === scenario.icon
                  ? 'bg-accent-50 ring-2 ring-accent-500 dark:bg-accent-700/20'
                  : 'bg-ink-50 hover:bg-ink-100 dark:bg-ink-700 dark:hover:bg-ink-600'
              }`}
            >
              {ic}
            </button>
          ))}
        </div>
      )}
      {pickerOpen === 'color' && (
        <div className="mt-3 flex flex-wrap gap-2">
          {COLOR_SWATCHES.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => {
                onColor(c);
                setPickerOpen(null);
              }}
              className={`h-8 w-8 rounded-full transition ${
                c === scenario.color ? 'ring-2 ring-offset-2 ring-accent-500 dark:ring-offset-ink-800' : ''
              }`}
              style={{ backgroundColor: c }}
              title={c}
              aria-label={`Color ${c}`}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Events timeline ─────────────────────────────────────────────────────────

function EventsTimeline({
  events,
  editingId,
  onEdit,
  onChange,
  onToggleEnabled,
  onDelete,
  onCloseEditor,
  calendarYearOf,
  ageOf,
}: {
  events: EventItem[];
  editingId: string | null;
  onEdit: (id: string) => void;
  onChange: (e: EventItem) => void;
  onToggleEnabled: (e: EventItem) => void;
  onDelete: (id: string) => void;
  onCloseEditor: () => void;
  calendarYearOf: (y: number) => number;
  ageOf: (y: number) => number;
}) {
  const sorted = [...events].sort((a, b) => a.yearOffset - b.yearOffset);
  // Group by yearOffset for visual bands
  const byYear = new Map<number, EventItem[]>();
  for (const e of sorted) {
    const list = byYear.get(e.yearOffset) ?? [];
    list.push(e);
    byYear.set(e.yearOffset, list);
  }
  const years = Array.from(byYear.keys()).sort((a, b) => a - b);

  return (
    <ul className="divide-y divide-ink-100 dark:divide-ink-700">
      {years.map(yr => (
        <li key={yr} className="flex gap-3 px-3 py-3">
          <div className="flex w-16 shrink-0 flex-col items-end pt-1 text-right">
            <div className="font-mono text-xs font-bold tabular-nums text-ink-900 dark:text-ink-50">
              {calendarYearOf(yr)}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-ink-500 dark:text-ink-300">
              age {ageOf(yr)}
            </div>
          </div>
          <ul className="flex-1 space-y-1.5">
            {byYear.get(yr)!.map(e => {
              const meta = EVENT_META[e.kind];
              const isEditing = editingId === e.id;
              return (
                <li key={e.id}>
                  <div
                    className={`rounded-xl border transition ${
                      isEditing
                        ? 'border-accent-500 bg-white shadow-card dark:bg-ink-800'
                        : 'border-ink-100 bg-white dark:border-ink-700 dark:bg-ink-800'
                    } ${e.enabled ? '' : 'opacity-60'}`}
                  >
                    <div className="flex items-center gap-2 px-3 py-2">
                      <Toggle
                        checked={e.enabled}
                        onChange={() => onToggleEnabled(e)}
                        ariaLabel="Event enabled"
                        size="sm"
                      />
                      <button
                        type="button"
                        onClick={() => onEdit(e.id)}
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                      >
                        <span
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                          style={{
                            backgroundColor: `${toneHex(meta.tone)}22`,
                            color: toneHex(meta.tone),
                          }}
                          aria-hidden
                        >
                          <Icon name={meta.icon} size={14} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className={`block truncate text-sm font-semibold ${!e.enabled ? 'line-through' : ''}`}>
                            {('name' in e && e.name) || meta.label}
                          </span>
                          <span className="block truncate text-[11px] text-ink-500 dark:text-ink-300">
                            {meta.label} · {eventHeadline(e)}
                          </span>
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => onEdit(e.id)}
                        className="shrink-0 rounded-md p-1 text-ink-400 hover:bg-ink-100 hover:text-ink-700 dark:hover:bg-ink-700 dark:hover:text-ink-100"
                        aria-label={isEditing ? 'Close' : 'Edit'}
                      >
                        <Icon name={isEditing ? 'x' : 'edit'} size={14} />
                      </button>
                    </div>
                    {isEditing && (
                      <div className="border-t border-ink-100 bg-ink-50/30 px-3 py-3 dark:border-ink-700 dark:bg-ink-800/40">
                        <EventEditor
                          event={e}
                          onChange={onChange}
                          onDelete={() => onDelete(e.id)}
                          onClose={onCloseEditor}
                        />
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </li>
      ))}
    </ul>
  );
}

function toneHex(tone: string): string {
  switch (tone) {
    case 'pos': return '#10b981';
    case 'neg': return '#ef4444';
    case 'accent': return '#4f6bff';
    case 'warning': return '#f59e0b';
    default: return '#64748b';
  }
}

// ── Add event picker (grouped) ──────────────────────────────────────────────

function EventKindPicker({
  onPick,
  onClose,
}: {
  onPick: (kind: EventItem['kind']) => void;
  onClose: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <Label>Add event</Label>
        <button
          type="button"
          onClick={onClose}
          className="text-[11px] font-semibold text-ink-500 hover:text-ink-700 dark:text-ink-300"
        >
          Cancel
        </button>
      </div>
      {EVENT_KINDS_BY_GROUP.map(group => (
        <div key={group.group} className="space-y-1.5">
          <Label>{group.label}</Label>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {group.kinds.map(k => {
              const meta = EVENT_META[k];
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => onPick(k)}
                  className="flex items-center gap-2 rounded-xl border border-ink-100 bg-white p-2.5 text-left transition hover:border-accent-500 hover:shadow-sm dark:border-ink-700 dark:bg-ink-800"
                >
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${toneHex(meta.tone)}22`, color: toneHex(meta.tone) }}
                  >
                    <Icon name={meta.icon} size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold">{meta.label}</div>
                    <div className="truncate text-[11px] text-ink-500 dark:text-ink-300">
                      {meta.blurb}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Template picker (for creating a new scenario) ──────────────────────────

function TemplatePicker({
  onPick,
  onClose,
}: {
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  return (
    <Card>
      <div className="mb-2 flex items-baseline justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
            New scenario
          </div>
          <div className="mt-0.5 text-[11px] text-ink-500 dark:text-ink-300">
            Start blank or pick a template
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-[11px] font-semibold text-ink-500 hover:text-ink-700 dark:text-ink-300"
        >
          Close
        </button>
      </div>
      <ul className="space-y-1.5">
        {TEMPLATES.map(t => (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => onPick(t.id)}
              className="flex w-full items-center gap-3 rounded-xl border border-ink-100 bg-white p-2.5 text-left transition hover:border-accent-500 hover:shadow-sm dark:border-ink-700 dark:bg-ink-800"
            >
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-base"
                style={{ backgroundColor: `${t.color}22`, color: t.color }}
                aria-hidden
              >
                {t.icon}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold">{t.name}</div>
                <div className="truncate text-[11px] text-ink-500 dark:text-ink-300">
                  {t.description}
                </div>
              </div>
              <Icon name="chevron-right" size={14} className="text-ink-400" />
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
